import { useState, type ReactElement } from 'react';

import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useToast } from '@/components/ui/toast';
import type { Role } from '@/features/roles-access/types';

import { adminActionError, userDisplayName } from '../presentation';
import type { AdminUser } from '../types';
import { useAssignRole, useRemoveRole, useSetUserStatus } from '../use-users';
import { UserRow, type UserRowPolicy } from './UserRow';

interface Props {
  users: AdminUser[];
  roles: Role[];
  meId: string | null;
  crossCompany: boolean;
  canManageRoles: boolean;
}

type Pending =
  | { kind: 'remove-role'; user: AdminUser; roleCode: string; roleName: string }
  | { kind: 'deactivate'; user: AdminUser };

/** Tabla del directorio + acciones (roles, desactivar, reactivar) con feedback
 *  inmediato y confirmación antes de lo destructivo (UX-FB-01). El backend es el
 *  guardián real; acá solo se evita ofrecer lo que va a rechazar. */
export function UserDirectory({
  users,
  roles,
  meId,
  crossCompany,
  canManageRoles,
}: Props): ReactElement {
  const assign = useAssignRole();
  const remove = useRemoveRole();
  const setStatus = useSetUserStatus();
  const { showToast } = useToast();
  const [pending, setPending] = useState<Pending | null>(null);

  const busyUserId = assign.isPending
    ? assign.variables?.userId
    : remove.isPending
      ? remove.variables?.userId
      : setStatus.isPending
        ? setStatus.variables?.userId
        : undefined;

  function policyFor(u: AdminUser): UserRowPolicy {
    return {
      isSelf: u.id === meId,
      // Misma regla que el backend (contención H1, admin/service.ts): el usuario tiene
      // empresas fuera de las del administrador. Las otras empresas del admin no bloquean.
      lockedMultiCompany: !crossCompany && u.otherCompaniesCount > 0,
      canManageRoles,
    };
  }

  function handleAssign(user: AdminUser, roleId: string): void {
    const roleName = roles.find((r) => r.id === roleId)?.name ?? 'seleccionado';
    const name = userDisplayName(user);
    assign.mutate(
      { userId: user.id, roleId },
      {
        onSuccess: () => showToast(`Rol ${roleName} asignado a ${name}`),
        onError: (e) =>
          showToast(
            adminActionError(e, `No se pudo asignar el rol a ${name}. Intenta de nuevo.`),
            'error',
          ),
      },
    );
  }

  function handleReactivate(user: AdminUser): void {
    const name = userDisplayName(user);
    setStatus.mutate(
      { userId: user.id, action: 'reactivate' },
      {
        onSuccess: () => showToast(`${name} fue reactivado.`),
        onError: (e) =>
          showToast(
            adminActionError(e, `No se pudo reactivar a ${name}. Intenta de nuevo.`),
            'error',
          ),
      },
    );
  }

  function confirmPending(): void {
    if (!pending) {
      return;
    }
    const name = userDisplayName(pending.user);
    const done = { onSettled: () => setPending(null) };
    if (pending.kind === 'deactivate') {
      setStatus.mutate(
        { userId: pending.user.id, action: 'deactivate' },
        {
          ...done,
          onSuccess: () => showToast(`${name} fue desactivado. Ya no puede ingresar al QMS.`),
          onError: (e) =>
            showToast(
              adminActionError(e, `No se pudo desactivar a ${name}. Intenta de nuevo.`),
              'error',
            ),
        },
      );
      return;
    }
    const roleId = roles.find((r) => r.code === pending.roleCode)?.id;
    if (!roleId) {
      showToast('No se pudo identificar el rol a quitar. Recarga la página.', 'error');
      setPending(null);
      return;
    }
    const { roleName } = pending;
    remove.mutate(
      { userId: pending.user.id, roleId },
      {
        ...done,
        onSuccess: () => showToast(`Rol ${roleName} quitado a ${name}`),
        onError: (e) =>
          showToast(
            adminActionError(e, `No se pudo quitar el rol a ${name}. Intenta de nuevo.`),
            'error',
          ),
      },
    );
  }

  const pendingName = pending ? userDisplayName(pending.user) : '';

  return (
    <>
      <div className="t-scroll">
        <table className="t t-cards">
          <caption className="sr-only">Usuarios de la empresa activa</caption>
          <thead>
            <tr>
              <th scope="col">Usuario</th>
              <th scope="col">Empresas</th>
              <th scope="col">Estado</th>
              <th scope="col">Roles</th>
              <th scope="col" className="t-act">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                roles={roles}
                policy={policyFor(u)}
                busy={busyUserId === u.id}
                onAssign={(roleId) => handleAssign(u, roleId)}
                onRemoveRequest={(roleCode, roleName) =>
                  setPending({ kind: 'remove-role', user: u, roleCode, roleName })
                }
                onDeactivateRequest={() => setPending({ kind: 'deactivate', user: u })}
                onReactivate={() => handleReactivate(u)}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={pending !== null}
        title={pending?.kind === 'deactivate' ? 'Desactivar usuario' : 'Quitar rol'}
        message={
          pending?.kind === 'deactivate'
            ? `¿Desactivar a ${pendingName}? Perderá el acceso al QMS de inmediato. Sus registros y firmas se conservan, y puedes reactivarlo cuando quieras.`
            : pending
              ? `¿Quitar el rol ${pending.roleName} de ${pendingName}? Perderá los permisos que ese rol otorga.`
              : ''
        }
        confirmLabel={pending?.kind === 'deactivate' ? 'Desactivar usuario' : 'Quitar rol'}
        destructive
        busy={remove.isPending || setStatus.isPending}
        onConfirm={confirmPending}
        onCancel={() => setPending(null)}
      />
    </>
  );
}
